// File: frontend/src/utils/fanTheme.ts
/**
 * Fan theme ("taraftar") colour engine.
 *
 * The taraftar theme is the only dynamic theme in the app: instead of a fixed
 * palette hard-coded in Variables.css, it is derived at runtime from the colours
 * of the user's FIRST favourite club (the same club whose colours already paint
 * the stripe under the profile banner).
 *
 * Static themes declare their palette inside a [data-theme="..."] block. This one
 * cannot, because the source colours are per-user, so the derived palette is
 * written as inline custom properties on <html>, which win over the CSS block by
 * specificity. The [data-theme="taraftar"] block in Variables.css still exists as
 * a safe fallback for the brief moment before this module runs (or if the club has
 * no colours stored at all).
 *
 * Nothing here touches component CSS: every variable written below is one the
 * other themes already define, so the whole app follows along automatically.
 */

// ── Types ──────────────────────────────────────────────────────────────────

/** Raw club colours as stored on the club record (any of them may be null). */
export interface FanClubColorSource {
  color1: string | null;
  color2: string | null;
  color3: string | null;
}

/** Cached payload: the colours plus the club they came from (for the picker). */
export interface FanThemeCache {
  clubId: number | null;
  clubName: string | null;
  colors: string[];
}

// localStorage key holding the last known fan colours, so the theme can paint
// itself on the very first frame after a reload without waiting for the API.
export const FAN_THEME_STORAGE_KEY = "fan-theme-colors";

// ── Low level colour helpers ───────────────────────────────────────────────

/** Normalizes "#abc" / "abc" / "#AABBCC" to "#aabbcc"; returns null if unusable. */
export function normalizeHex(value: string | null | undefined): string | null {
  if (!value) return null;
  let hex = value.trim().replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return `#${hex.toLowerCase()}`;
}

function toRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function toHex(rgb: [number, number, number]): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  return `#${rgb.map((c) => clamp(c).toString(16).padStart(2, "0")).join("")}`;
}

/** Mixes two colours: amount 0 returns a, amount 1 returns b. */
function mix(a: string, b: string, amount: number): string {
  const [r1, g1, b1] = toRgb(a);
  const [r2, g2, b2] = toRgb(b);
  const t = Math.max(0, Math.min(1, amount));
  return toHex([r1 + (r2 - r1) * t, g1 + (g2 - g1) * t, b1 + (b2 - b1) * t]);
}

const WHITE = "#ffffff";
const BLACK = "#000000";

/** Pulls a colour toward white. */
function lighten(hex: string, amount: number): string {
  return mix(hex, WHITE, amount);
}

/** Pulls a colour toward black. */
function darken(hex: string, amount: number): string {
  return mix(hex, BLACK, amount);
}

/** WCAG relative luminance (0 = black, 1 = white). */
function luminance(hex: string): number {
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const [r, g, b] = toRgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two colours (1 = identical, 21 = black on white). */
function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  const light = Math.max(la, lb);
  const dark = Math.min(la, lb);
  return (light + 0.05) / (dark + 0.05);
}

/** Builds an rgba() string from a hex colour and an alpha value. */
function alpha(hex: string, a: number): string {
  const [r, g, b] = toRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/**
 * Darkens a colour step by step until it carries white text at the given
 * contrast ratio. Club colours are often bright (yellow, sky blue, orange);
 * used raw as --color-primary they would make every button and link unreadable.
 */
function darkenUntilReadableOnWhiteText(hex: string, target = 4.5): string {
  let out = hex;
  let guard = 0;
  while (contrast(out, WHITE) < target && guard < 20) {
    out = darken(out, 0.08);
    guard += 1;
  }
  return out;
}

/**
 * Lightens a colour step by step until it is readable ON TOP of the given
 * background. Used for the header's secondary text, which sits on the club's
 * dark colour and must not disappear into it.
 */
function lightenUntilReadableOn(hex: string, background: string, target = 3.5): string {
  let out = hex;
  let guard = 0;
  while (contrast(out, background) < target && guard < 20) {
    out = lighten(out, 0.1);
    guard += 1;
  }
  return out;
}

/** Distance between the strongest and weakest RGB channel — 0 means pure grey. */
function chroma(hex: string): number {
  const [r, g, b] = toRgb(hex);
  return Math.max(r, g, b) - Math.min(r, g, b);
}

// Body text colour of the neutral base the fan theme sits on. Links are
// compared against it so they never blend into bold running text.
const BODY_TEXT = "#333333";
// Default link blue of the light theme, used for clubs whose colours are
// greyscale (black/white kits): a near-black link is indistinguishable from
// bold text no matter how it is tuned, so those clubs keep a coloured link.
const NEUTRAL_LINK = "#3a7bd5";
// A link colour must clear this contrast ratio against body text to read as
// a link at a glance.
const LINK_SEPARATION = 2.0;

/**
 * Picks the link colour for a club.
 *
 * Coloured clubs (red, claret, navy...) keep their own colour: hue alone makes
 * the link obvious next to black bold text. Greyscale clubs fall back to the
 * default blue, because there is no shade of grey that reads as a link.
 */
function pickLinkColor(candidates: string[]): string {
  const colored = candidates.filter((c) => chroma(c) >= 30);
  if (colored.length === 0) return NEUTRAL_LINK;

  // Prefer the club colour that stands apart from body text the most.
  const best = colored
    .map((c) => darkenUntilReadableOnWhiteText(c))
    .sort((a, b) => contrast(b, BODY_TEXT) - contrast(a, BODY_TEXT))[0];

  if (contrast(best, BODY_TEXT) >= LINK_SEPARATION) return best;

  // Close in tone but clearly coloured — lift it slightly, staying readable
  // on a white surface.
  let out = best;
  let guard = 0;
  while (contrast(out, BODY_TEXT) < LINK_SEPARATION && contrast(out, WHITE) > 4.5 && guard < 12) {
    out = lighten(out, 0.08);
    guard += 1;
  }
  return out;
}

/**
 * Builds the kit stripe painted along the bottom edge of the header: equal
 * hard-stop segments, one per colour, like the band on a shirt. This is what
 * actually puts a club's secondary colour on screen — the header background can
 * only ever show one colour, so without the stripe the yellow of Galatasaray or
 * Fenerbahçe never appears anywhere.
 */
function buildStripe(colors: string[]): string {
  const segment = 100 / colors.length;
  const stops = colors.map((color, i) => {
    const from = (segment * i).toFixed(4);
    const to = (segment * (i + 1)).toFixed(4);
    return `${color} ${from}% ${to}%`;
  });
  return `linear-gradient(to right, ${stops.join(", ")})`;
}

/** Forces a colour dark enough to serve as a header/banner background. */
function forceHeaderDark(hex: string): string {
  let out = hex;
  let guard = 0;
  // A club record with only white (or no usable dark colour) must never end up
  // painting a white header with white text on it.
  while (luminance(out) > 0.3 && guard < 20) {
    out = darken(out, 0.12);
    guard += 1;
  }
  return out;
}

// ── Colour extraction ──────────────────────────────────────────────────────

/**
 * Returns the usable, normalized colours of a club in declaration order.
 * Clubs with no verified colours stored return an empty array — those clubs
 * cannot drive the theme.
 */
export function extractClubColors(club: FanClubColorSource | null | undefined): string[] {
  if (!club) return [];
  return [club.color1, club.color2, club.color3]
    .map(normalizeHex)
    .filter((c): c is string => c !== null);
}

// ── Palette derivation ─────────────────────────────────────────────────────

/**
 * Every CSS custom property the theme writes, as plain name/value pairs.
 * Keys match the variables the static themes declare in Variables.css.
 */
type Palette = Record<string, string>;

/**
 * How much of the club colour is allowed to bleed into page surfaces.
 *
 * Deliberately 0: an early version tinted every background with the club colour
 * and the result was exhausting to read — a full red page for Galatasaray, a
 * full yellow one for Fenerbahçe. The page body, entry cards, borders and greys
 * therefore stay on the neutral light palette, exactly like the light theme, and
 * the club identity is carried by the accents only: header, links, buttons,
 * usernames, active states.
 *
 * Raise it (0.04–0.08 is the sane range) if a soft tint is ever wanted back;
 * everything else adapts automatically.
 */
const SURFACE_TINT: number = 0;

function buildPalette(colors: string[]): Palette {
  // Sort by brightness: the darkest colour carries white text (header, buttons,
  // links), the brightest becomes the highlight sitting on top of it.
  const sorted = [...colors].sort((a, b) => luminance(a) - luminance(b));
  const darkest = sorted[0];
  const brightest = sorted[sorted.length - 1];

  // Primary: club's dark colour, forced dark enough to carry white text.
  // Bright club colours (yellow, sky blue, orange) used raw here would make
  // every button and link unreadable.
  const primary = darkenUntilReadableOnWhiteText(darkest);
  const primaryHover = darken(primary, 0.15);

  // Header keeps the club's dark colour with its bright colour as the accent,
  // lifted until it is legible against that background.
  const headerBg = forceHeaderDark(primary);
  const headerAccent = lightenUntilReadableOn(brightest, headerBg);

  // Stripe colours: everything that is visibly different from the header
  // background. Dropping the near-identical ones avoids a "stripe" that is just
  // the header colour repeated.
  const stripeColors = colors.filter((c) => contrast(c, headerBg) >= 1.8);
  const stripe = buildStripe(stripeColors.length > 0 ? stripeColors : [headerAccent]);

  // Link colour is chosen separately from --color-primary: buttons only need to
  // carry white text, links additionally need to stand out from bold body text.
  const link = pickLinkColor(sorted);

  const palette: Palette = {
    // Raw club colours, exposed for components that want them directly
    "--fan-color-1": colors[0] ?? primary,
    "--fan-color-2": colors[1] ?? colors[0] ?? primary,
    "--fan-color-3": colors[2] ?? colors[1] ?? colors[0] ?? primary,

    // Primary — the one variable most components read for their accent colour
    "--color-primary": primary,
    "--color-primary-hover": primaryHover,
    "--color-primary-light": alpha(primary, 0.1),
    "--color-primary-lighter": alpha(primary, 0.05),
    "--color-primary-border": alpha(primary, 0.22),
    "--color-primary-shadow": alpha(primary, 0.25),

    // Links and usernames follow the club instead of the default blue
    "--text-link": link,
    "--text-link-hover": darken(link, 0.15),
    "--text-username": link,

    // Hover wash picks up a hint of the club colour without tinting the surface
    "--bg-hover": alpha(primary, 0.05),
    "--bg-action-box-active": lighten(primary, 0.35),

    // Header — the strongest club statement on the page
    "--header-bg": headerBg,
    "--header-text": "#ffffff",
    "--header-text-secondary": headerAccent,
    "--header-icon": "#ffffff",

    // Kit stripe along the bottom of the header, and the same accent as the
    // dividing line under the gündem banner.
    "--header-accent-bar": stripe,
    "--topic-banner-border": headerAccent,

    // Custom topic (gündem) banner — neutral dark by default because those
    // topics carry no club identity; under the fan theme it follows the club.
    "--topic-banner-gradient": `linear-gradient(135deg, ${lighten(headerBg, 0.12)} 0%, ${darken(headerBg, 0.25)} 100%)`,

    // Brand gradient endpoints follow the club instead of the fixed orange
    "--color-brand-primary": primary,
    "--color-brand-secondary": primaryHover,
    "--gradient-primary": `linear-gradient(to right, ${headerAccent}, ${primary})`,
  };

  // Optional surface tint. Left off by default (see SURFACE_TINT above); when
  // switched on it only nudges the neutral backgrounds, never the text colours.
  if (SURFACE_TINT > 0) {
    const tint = luminance(brightest) < 0.12 ? lighten(brightest, 0.55) : brightest;
    palette["--bg-body"] = mix(WHITE, tint, SURFACE_TINT);
    palette["--bg-secondary"] = mix(WHITE, tint, SURFACE_TINT * 0.8);
    palette["--bg-light"] = mix(WHITE, tint, SURFACE_TINT * 0.5);
    palette["--bg-lighter"] = mix(WHITE, tint, SURFACE_TINT * 0.4);
    palette["--bg-entry-hover"] = mix(WHITE, tint, SURFACE_TINT * 0.6);
  }

  return palette;
}

// ── Applying / clearing ────────────────────────────────────────────────────

// Kept so clearFanTheme() removes exactly what was written, nothing else.
let appliedProperties: string[] = [];

/**
 * Writes the derived palette onto <html>. Returns false when the club has no
 * usable colours, in which case the caller must fall back to another theme.
 */
export function applyFanTheme(colors: string[]): boolean {
  const usable = colors.map(normalizeHex).filter((c): c is string => c !== null);
  if (usable.length === 0) return false;

  const palette = buildPalette(usable);
  const root = document.documentElement;

  clearFanTheme();
  Object.entries(palette).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });
  appliedProperties = Object.keys(palette);
  return true;
}

/** Removes every property written by applyFanTheme, restoring the CSS themes. */
export function clearFanTheme(): void {
  const root = document.documentElement;
  appliedProperties.forEach((name) => root.style.removeProperty(name));
  appliedProperties = [];
}

// ── Cache ──────────────────────────────────────────────────────────────────

/** Reads the last known fan colours, or null when nothing is cached yet. */
export function loadFanThemeCache(): FanThemeCache | null {
  try {
    const raw = localStorage.getItem(FAN_THEME_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FanThemeCache;
    if (!parsed || !Array.isArray(parsed.colors) || parsed.colors.length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Stores the fan colours; passing null clears the cache (e.g. on logout). */
export function saveFanThemeCache(cache: FanThemeCache | null): void {
  try {
    if (!cache || cache.colors.length === 0) {
      localStorage.removeItem(FAN_THEME_STORAGE_KEY);
      return;
    }
    localStorage.setItem(FAN_THEME_STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // Storage can be unavailable (private mode, quota) — the theme still works
    // for the current session, it just cannot pre-paint after a reload.
  }
}